import { BaseGym } from '../types/Gym';

type GymJsonDataSpec = {
  '@context': string;
  '@id': string;
  '@type': string;
  name: string;
  description: string;
  url: string;
  priceRange: string;
  geo: {
    '@type': 'GeoCoordinates';
    latitude: string;
    longitude: string;
    address: string;
  };
  address: {
    '@type': 'PostalAddress';
    addressCountry: 'uk';
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    streetAddress: string;
  };
  telephone: string;
  image: {
    '@context': 'https://schema.org/';
    '@type': 'ImageObject';
    '@id': '#MainGymImage';
    name: string;
    url: string;
    author: false;
    contentUrl: string;
    contentLocation: string;
  };
  amenityFeature: Array<{
    '@type': string;
    value: string;
    name: string;
  }>;
  aggregateRating?: {
    '@type': 'AggregateRating';
    ratingValue: string;
    reviewCount: string;
    bestRating: '5';
    worstRating: '0';
  };
  review?: {
    '@type': 'Review';
    author: {
      '@type': 'Person';
      name: string;
    };
    reviewBody: string;
    reviewRating: {
      '@type': 'Rating';
      ratingValue: number;
    }
  };
};

export const makeldJsonSchema = (gym: BaseGym): GymJsonDataSpec => ({
  '@context': 'http://schema.org',
  '@id': `https://www.hussle.com/${gym.b2c_gym_url}`,
  '@type': 'ExerciseGym',
  name: gym.name,
  description: gym.description,
  url: `https://www.hussle.com/${gym.b2c_gym_url}`,
  priceRange: `from: &pound;${(gym.products && Math.min(...gym.products.map((p) => p.price)) / 100).toFixed(2)}`,
  geo: {
    '@type': 'GeoCoordinates',
    latitude: gym.location.latitude.toString(),
    longitude: gym.location.longitude.toString(),
    address: gym.location.street_address + ', ' + gym.location.locality + ', ' + gym.location.postcode,
  },
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'uk',
    addressLocality: gym.location.locality,
    addressRegion: '',
    postalCode: gym.location.postcode,
    streetAddress: gym.location.street_address + ', ' + gym.location.locality,
  },
  telephone: gym.telephone,
  image: {
    '@context': 'https://schema.org/',
    '@type': 'ImageObject',
    '@id': '#MainGymImage',
    name: 'Primary image',
    url: `${process.env.NEXT_PUBLIC_STATIC_DOMAIN}/gym_img/465x335/${gym.id}/${gym.thumbnail}`,
    author: false,
    contentUrl: `https://www.hussle.com${gym.b2c_gym_url}`,
    contentLocation: gym.location.street_address + ', ' + gym.location.locality + ', ' + gym.location.postcode,
  },
  amenityFeature:
    gym.resources?.map((resource) => {
      return {
        '@type': 'LocationFeatureSpecification',
        value: 'True',
        name: resource.name,
      };
    }) || [],
  ...(gym.feedback.count > 0 && {
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: gym.feedback.score.toFixed(1),
      reviewCount: gym.feedback.count.toString(),
      bestRating: '5',
      worstRating: '0',
    }
  }),
  ...(gym.reviews.length > 0 && {
    review: {
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: gym.reviews[0].author
      },
      reviewBody: gym.reviews[0].comment,
      reviewRating: {
        '@type': 'Rating',
        ratingValue: gym.reviews[0].score
      }
    }
  }),
});
